import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ActorTree } from '../types/actor-types';
import { servicesContainer } from '@/core/di/services-container';

interface ActorStore {
  // Data state
  actors: ActorTree[];
  totalCount: number;
  selectedOperatorId: string | null;
  
  // Loading states
  isLoading: boolean;
  
  // Error states  
  error: string | null;
  
  // Metadata
  lastUpdated: Date | null;
  
  // Actions
  loadActorTree: (operatorId: string) => Promise<void>;
  loadActorTreeWithCount: (operatorId: string) => Promise<void>;
  setSelectedOperator: (operatorId: string | null) => void;
  refreshActorTree: () => Promise<void>;
  clearError: () => void;
  clearData: () => void;
}

export const useActorStore = create<ActorStore>()(
  persist(
    (set, get) => ({
      // Initial state
      actors: [],
      totalCount: 0,
      selectedOperatorId: null,
      isLoading: false,
      error: null,
      lastUpdated: null,

      loadActorTree: async (operatorId: string) => {
        // Don't reload if we already have data for this operator or if currently loading
        const state = get();
        if (state.isLoading) {
          return;
        }
        // Skip if we already loaded data for this operator (even if 0 actors returned)
        if (state.selectedOperatorId === operatorId && state.lastUpdated && !state.error) {
          return;
        }
        
        if (state.error && state.selectedOperatorId === operatorId) {
          console.warn('ActorStore.loadActorTree: Previous error exists for operator', operatorId, '- skipping retry. Call clearError() to retry.');
          return;
        }

        set({ isLoading: true, error: null, selectedOperatorId: operatorId });

        try {
          // Get services lazily to avoid circular dependency
          const services = servicesContainer.getServices();
          const actors = await services.getActorTreeUseCase.execute(operatorId);

          set({
            actors,
            totalCount: actors.length,
            isLoading: false,
            lastUpdated: new Date(),
            error: null,
          });
        } catch (error) {
          console.error('ActorStore.loadActorTree: Failed:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load actor tree',
            actors: [],
            totalCount: 0,
          });
        }
      },

      loadActorTreeWithCount: async (operatorId: string) => {
        // Don't reload if we already have data for this operator or if currently loading
        const state = get();
        if (state.isLoading) {
          return;
        }
        // Skip if we already loaded data for this operator (even if 0 actors returned)
        if (state.selectedOperatorId === operatorId && state.lastUpdated && !state.error) {
          return;
        }
        
        if (state.error && state.selectedOperatorId === operatorId) {
          console.warn('ActorStore.loadActorTreeWithCount: Previous error exists for operator', operatorId, '- skipping retry. Call clearError() to retry.');
          return;
        }

        set({ isLoading: true, error: null, selectedOperatorId: operatorId });

        try {
          // Get services lazily to avoid circular dependency
          const services = servicesContainer.getServices();
          const result = await services.getActorTreeUseCase.executeWithCount(operatorId);

          set({
            actors: result.actors,
            totalCount: result.totalCount,
            isLoading: false,
            lastUpdated: new Date(),
            error: null,
          });
        } catch (error) {
          console.error('ActorStore.loadActorTreeWithCount: Failed:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to load actor tree with count',
            actors: [],
            totalCount: 0,
          });
        }
      },

      setSelectedOperator: (operatorId: string | null) => {
        set({ selectedOperatorId: operatorId, lastUpdated: null });

        // Clear data if no operator is selected
        if (!operatorId) {
          set({
            actors: [],
            totalCount: 0,
            error: null,
          });
        }
      },

      refreshActorTree: async () => {
        const state = get();
        
        // Prevent multiple simultaneous refresh calls
        if (state.isLoading) {
          return;
        }
        
        if (!state.selectedOperatorId) {
          set({ error: 'No operator selected for refresh' });
          return;
        }

        // Force reload by clearing current data first
        set({ 
          actors: [], 
          totalCount: 0,
          isLoading: true, 
          error: null 
        });

        try {
          // Get services lazily to avoid circular dependency
          const services = servicesContainer.getServices();
          const { selectedOperatorId } = get();
          const actors = await services.getActorTreeUseCase.execute(selectedOperatorId!);

          set({
            actors,
            totalCount: actors.length,
            isLoading: false,
            lastUpdated: new Date(),
            error: null,
          });
        } catch (error) {
          console.error('ActorStore.refreshActorTree: Failed:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to refresh actor tree',
            actors: [],
            totalCount: 0,
          });
        }
      },

      clearError: () => set({ error: null }),

      clearData: () => set({
        actors: [],
        totalCount: 0,
        selectedOperatorId: null,
        error: null,
        lastUpdated: null,
      }),
    }),
    {
      name: 'actor-storage',
      partialize: (state) => ({
        // Don't persist data, only persist the selected operator context
        selectedOperatorId: state.selectedOperatorId,
      }),
    }
  )
);